import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { FaUserEdit, FaCamera, FaKey, FaSave, FaEnvelope, FaShieldAlt, FaMapMarkerAlt } from 'react-icons/fa';
import { showSuccess, showError, showLoading, closeLoading } from '../../../utils/Alert';
import Loader from '../../shared/Loader';
import { API_URL } from '../../../config';
import './ProfileSection.css';

const ProfileSection = () => {
    const [loading, setLoading] = useState(true);
    const [profile, setProfile] = useState({
        name: '',
        email: '',
        phone: '',
        role: '',
        location: '',
        bio: '',
        profileImage: ''
    });

    const fileInputRef = React.useRef(null);

    const [passwords, setPasswords] = useState({
        current: '',
        newPass: '',
        confirmPass: ''
    });

    useEffect(() => {
        const fetchProfile = async () => {
            try {
                const res = await axios.get('/api/users/me', {
                    headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
                });
                setProfile({
                    name: res.data.name || '',
                    email: res.data.email || '',
                    phone: res.data.phone || '',
                    role: res.data.role || 'Super Admin',
                    location: res.data.location || '',
                    bio: res.data.bio || '',
                    profileImage: res.data.profileImage || ''
                });
                setTimeout(() => {
                    setLoading(false);
                }, 500);
            } catch (err) {
                console.error("Error fetching profile", err);
                setTimeout(() => {
                    setLoading(false);
                }, 500);
            }
        };
        fetchProfile();
    }, []);

    const handleProfileChange = (e) => {
        setProfile({ ...profile, [e.target.name]: e.target.value });
    };

    const handlePasswordChange = (e) => {
        setPasswords({ ...passwords, [e.target.name]: e.target.value });
    };

    const handleAvatarClick = () => {
        if (fileInputRef.current) {
            fileInputRef.current.click();
        }
    };

    const handleImageUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        const formData = new FormData();
        formData.append('image', file);

        showLoading('Uploading Image...');
        try {
            const res = await axios.post('/api/users/profile-image', formData, {
                headers: {
                    Authorization: `Bearer ${localStorage.getItem('token')}`,
                    'Content-Type': 'multipart/form-data'
                }
            });
            setProfile(prev => ({ ...prev, profileImage: res.data.profileImage }));
            closeLoading();
            showSuccess('Upload Success', 'Your profile image has been updated.');
            window.dispatchEvent(new Event('profileUpdated'));
        } catch (err) {
            console.error('Error uploading image', err);
            closeLoading();
            showError('Upload Failed', 'Could not save profile image.');
        }
    };

    const saveProfile = async (e) => {
        e.preventDefault();
        showLoading('Updating your profile...');
        try {
            await axios.put('/api/users/profile', profile, {
                headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
            });
            closeLoading();
            showSuccess('Profile Updated', 'Your changes have been saved securely.');
            window.dispatchEvent(new Event('profileUpdated'));
        } catch (err) {
            console.error(err);
            closeLoading();
            showError('Update Failed', 'There was an error updating your profile details.');
        }
    };

    const savePassword = async (e) => {
        e.preventDefault();
        if (passwords.newPass !== passwords.confirmPass) {
            return showError('Validation Error', 'New passwords do not match.');
        }
        showLoading('Updating account security...');
        try {
            await axios.post('/api/auth/change-password', {
                currentPassword: passwords.current,
                newPassword: passwords.newPass
            }, {
                headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
            });
            closeLoading();
            showSuccess('Security Updated', 'Your password has been changed successfully.');
            setPasswords({ current: '', newPass: '', confirmPass: '' });
        } catch (err) {
            console.error(err);
            closeLoading();
            showError('Security Update Failed', err.response?.data?.message || 'Failed to update password.');
        }
    };

    if (loading && !profile.email) {
        return <Loader />;
    }

    return (
        <div className="profile-section fade-in">
            <div className="dashboard-header">
                <h1 className="page-title">Administrative Profile</h1>
                <p className="page-subtitle">Personalizing your enterprise identity and security configurations.</p>
            </div>

            <div className="profile-grid">
                {/* Left Column: Avatar & Quick Info */}
                <div className="avatar-card text-center">
                    <div className="avatar-wrapper">
                        {profile.profileImage ? (
                            <img src={`${API_URL}${profile.profileImage}`} alt="Profile" className="profile-avatar-large-img" />
                        ) : (
                            <div className="profile-avatar-large">
                                {profile.name ? profile.name.charAt(0).toUpperCase() : 'A'}
                            </div>
                        )}
                        <input
                            type="file"
                            accept="image/*"
                            style={{ display: 'none' }}
                            ref={fileInputRef}
                            onChange={handleImageUpload}
                        />
                        <button className="upload-avatar-btn" title="Upload New Picture" onClick={handleAvatarClick}>
                            <FaCamera />
                        </button>
                    </div>
                    <h2 className="profile-name">{profile.name}</h2>
                    <p className="profile-role">{profile.role}</p>

                    <div className="profile-quick-stats">
                        <div className="stat-row">
                            <FaEnvelope className="stat-icon" />
                            <span>{profile.email}</span>
                        </div>
                        <div className="stat-row">
                            <FaMapMarkerAlt className="stat-icon" />
                            <span>{profile.location || 'Location not set'}</span>
                        </div>
                        <div className="stat-row">
                            <FaShieldAlt className="stat-icon" />
                            <span>Verified Super Admin Access</span>
                        </div>
                    </div>
                </div>

                {/* Right Column: General Settings */}
                <div className="form-card general-settings">
                    <h3 className="card-title"><FaUserEdit /> General Settings</h3>
                    <form onSubmit={saveProfile} className="profile-form">
                        <div className="form-group">
                            <label>Full Name</label>
                            <input
                                type="text" name="name" value={profile.name} onChange={handleProfileChange}
                                className="glass-input" required
                            />
                        </div>

                        <div className="form-row">
                            <div className="form-group">
                                <label>Email Address (Read Only)</label>
                                <input
                                    type="email" name="email" value={profile.email}
                                    className="glass-input" readOnly style={{ opacity: 0.7, cursor: 'not-allowed' }}
                                />
                            </div>
                            <div className="form-group">
                                <label>Phone Number</label>
                                <input
                                    type="text" name="phone" value={profile.phone} onChange={handleProfileChange}
                                    className="glass-input" placeholder="+91 XXXXX XXXXX"
                                />
                            </div>
                        </div>

                        <div className="form-group">
                            <label>Location</label>
                            <input
                                type="text" name="location" value={profile.location} onChange={handleProfileChange}
                                className="glass-input" placeholder="City, Country"
                            />
                        </div>

                        <div className="form-group">
                            <label>Bio / Description</label>
                            <textarea
                                name="bio" value={profile.bio} onChange={handleProfileChange} rows="3"
                                className="glass-input resize-none" placeholder="Tell us about your role..."
                            ></textarea>
                        </div>

                        <div className="form-actions">
                            <button type="submit" className="primary-btn pulse-on-hover">
                                <FaSave style={{ marginRight: '8px' }} /> Update Profile
                            </button>
                        </div>
                    </form>
                </div>

                {/* Bottom Row: Security (Full Width) */}
                <div className="form-card security-card">
                    <h3 className="card-title"><FaKey /> Security & Access</h3>
                    <form onSubmit={savePassword} className="profile-form">
                        <div className="form-row triplet">
                            <div className="form-group">
                                <label>Current Password</label>
                                <input
                                    type="password" name="current" value={passwords.current} onChange={handlePasswordChange}
                                    className="glass-input" required placeholder="Verify current"
                                />
                            </div>
                            <div className="form-group">
                                <label>New Password</label>
                                <input
                                    type="password" name="newPass" value={passwords.newPass} onChange={handlePasswordChange}
                                    className="glass-input" required placeholder="Min. 8 chars"
                                />
                            </div>
                            <div className="form-group">
                                <label>Confirm New Password</label>
                                <input
                                    type="password" name="confirmPass" value={passwords.confirmPass} onChange={handlePasswordChange}
                                    className="glass-input" required placeholder="Match new"
                                />
                            </div>
                        </div>

                        <div className="form-actions">
                            <button type="submit" className="primary-btn pulse-on-hover">
                                Securely Change Password
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default ProfileSection;
